// Shared API client for public site + CMS
// build.ts replaces __API_BASE_URL__ at build time.
// Empty string = same-origin /api (Docker nginx → backend:3000).

declare const __API_BASE_URL__: string | undefined;

export const API_BASE_URL: string =
  typeof __API_BASE_URL__ === 'string' ? __API_BASE_URL__ : 'http://localhost:3001';

export interface Project {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  coverUrl: string | null;
  galleries: string[];
  githubUrl: string | null;
  demoUrl: string | null;
  techStack: string[];
  featured: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface Certification {
  id: string;
  title: string;
  issuer: string;
  date: string;
  description: string | null;
  credentialUrl: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  title: string;
  date: string;
  location: string;
  description: string | null;
  imageUrl: string | null;
  link: string | null;
  createdAt: string;
  updatedAt: string;
}

type ApiFetchOptions = RequestInit & {
  json?: unknown;
};

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { json, headers: customHeaders, ...init } = options;

  const headers = new Headers(customHeaders);

  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  // Auth uses httpOnly cookie (credentials: 'include'); no token in JS storage
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
    body: json !== undefined ? JSON.stringify(json) : init.body,
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    const message =
      body && typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function fetchProjects(): Promise<Project[]> {
  return apiFetch<Project[]>('/api/projects');
}

export async function fetchCertifications(): Promise<Certification[]> {
  return apiFetch<Certification[]>('/api/certifications');
}

export async function fetchEvents(): Promise<Event[]> {
  return apiFetch<Event[]>('/api/events');
}

export async function submitContact(data: {
  name: string;
  email: string;
  message: string;
}): Promise<void> {
  await apiFetch('/api/contact', { method: 'POST', json: data });
}

export async function uploadFile(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);

  const data = await apiFetch<{ url: string }>('/api/cms/upload', {
    method: 'POST',
    body: form,
  });

  return data.url;
}

export async function deleteUploadedFile(url: string): Promise<void> {
  await apiFetch('/api/cms/upload', {
    method: 'DELETE',
    json: { url },
  });
}
